// Notes on a renewal (spec C7.4, S14, Record and Renewals boards): one note's line, and the Record's
// Notes card with its inline box. Every role that may open the renewal adds notes; a deleted renewal
// is read only. Append only: the toast's Undo takes back a note just added. The text shows as typed,
// as text, never as HTML.
import { useState } from 'react';
import type { Api, Note, NotesView } from '../auth/api';
import { NoteIcon } from '../shell/icons';
import { noteByline, noteMax, noteReady, saveNote } from './notes-lines';
import { useNoteToast, useNotes } from './use-notes';

/** A note keeps its line breaks and wraps a long word instead of widening the card. */
const wrapped = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;

export function NoteLine({ n }: { n: Note }) {
  return (
    <div className="qr" style={{ alignItems: 'flex-start', justifyContent: 'flex-start' }}>
      <span className="avatar sm" aria-hidden="true">
        {n.by.initials}
      </span>
      <div className="col g2 grow" style={{ minWidth: 0 }}>
        <span className="t12 muted">{noteByline(n)}</span>
        <p className="m0 t13 ink" style={wrapped}>
          {n.text}
        </p>
      </div>
    </div>
  );
}

/** The Notes card as it shows: newest first, then the inline box, or the read-only line. */
export function NotesCardView({
  view,
  failed,
  typed,
  busy,
  error,
  onType,
  onAdd,
}: {
  view: NotesView | null;
  failed: boolean;
  typed: string;
  busy: boolean;
  error: string | null;
  onType: (typed: string) => void;
  onAdd: () => void;
}) {
  const ready = noteReady(typed) !== null;
  return (
    <section className="card" aria-labelledby="notes-title">
      <div className="card-h">
        <h2 id="notes-title" className="card-t">
          <NoteIcon className="ico s14" />
          Notes
        </h2>
      </div>
      {failed ? (
        <div className="emp">
          <span>The notes could not be loaded. Reload the page to try again.</span>
        </div>
      ) : view && view.notes.length > 0 ? (
        <div>
          {view.notes.map((n) => (
            <NoteLine key={n.id} n={n} />
          ))}
        </div>
      ) : (
        view && (
          <div className="emp">
            <span>No notes yet.</span>
          </div>
        )
      )}
      {view?.readOnly ? (
        <div className="emp">
          <span>Read only. Restore the record to add notes.</span>
        </div>
      ) : (
        <div className="emp col g4" style={{ alignItems: 'stretch' }}>
          <div className="row g8">
            <label htmlFor="note-input" className="sr">
              Add a note
            </label>
            <input
              id="note-input"
              className="input"
              placeholder="Add a note"
              maxLength={noteMax}
              value={typed}
              disabled={!view}
              onChange={(e) => onType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAdd();
              }}
            />
            <button
              type="button"
              className="btn"
              disabled={!ready || busy || !view}
              onClick={onAdd}
            >
              Add
            </button>
          </div>
          {error && <p className="errl m0">{error}</p>}
        </div>
      )}
    </section>
  );
}

/** The Record's Notes card. */
export function NotesCard({
  api,
  renewalId,
  refresh = 0,
  onChanged,
}: {
  api: Api;
  renewalId: string;
  /** Goes up when the page refreshes on its own (issue 291): the notes are read again. */
  refresh?: number;
  /** A note was added or taken back: read the Portal log again. */
  onChanged: () => void;
}) {
  const [version, setVersion] = useState(0);
  const { view, failed } = useNotes(api, renewalId, version + refresh);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = () => {
    setVersion((v) => v + 1);
    onChanged();
  };
  const toast = useNoteToast(api, changed);

  const add = async () => {
    const text = noteReady(typed);
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    const res = await saveNote(api, renewalId, text);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setTyped('');
    toast.show(res.toast, res.undo);
    changed();
  };

  return (
    <>
      <NotesCardView
        view={view}
        failed={failed}
        typed={typed}
        busy={busy}
        error={error}
        onType={setTyped}
        onAdd={() => void add()}
      />
      {toast.element}
    </>
  );
}
